/**
 * Ein kanonischer Beispieldatensatz.
 *
 * Er existiert aus zwei Gruenden, und beide brauchen exakt dieselben Zahlen:
 * die Oberflaeche muss sich einmal mit realistischen Mengen ansehen lassen,
 * bevor sie an echte Konten geht, und die Emulatoren wollen befuellt werden.
 * Deshalb liegt der Datensatz hier statt in einem der beiden Aufrufer — zwei
 * Fassungen wuerden auseinanderlaufen.
 *
 * Enthaelt bewusst die Grenzfaelle, die im Alltag Aerger machen: eine
 * Teilzahlung, eine ueberfaellige Forderung, eine Sammelzahlung ueber mehrere
 * Rechnungen, Reverse-Charge auf beiden Seiten, einen Rechnungsentwurf der
 * nicht in die Prognose gehoert, und Kleinausgaben ohne Planung.
 */
import { expandFixedCost, mapBankTransaction, mapInvoice, mapPurchase } from "./ingest.js";
import { proposeMatches } from "./matching.js";
import { todayInBerlin } from "./dates.js";
import { computeVatPeriods, vatPlannedItems } from "./vat.js";
import { AUTO_MATCH_THRESHOLD, resolveMatchedCents, statusFor } from "./types.js";
export function buildDemoDataset() {
    const today = todayInBerlin();
    const day = (n) => new Date(new Date(`${today}T00:00:00Z`).getTime() + n * 86400000).toISOString().slice(0, 10);
    // ---- Quelldaten im Rohformat der beiden Systeme -----------------------------
    const invoicesRaw = [
        { id: 5101, identifier: "R2026-0151", title: "Markenrelaunch Phase 2", date: day(-52), due_date: day(-22),
            status: "overdue", net_total: 9705.88, gross_total: 11550, customer: { id: 301, name: "Weinberg & Co. KG" },
            payments: [{ date: day(-4), paid_total: 5000 }] },
        { id: 5104, identifier: "R2026-0188", title: "Website Hoffmann", date: day(-26), due_date: day(4),
            status: "sent", net_total: 12000, gross_total: 14280, customer: { id: 302, name: "Hoffmann Bau GmbH" } },
        { id: 5107, identifier: "R2026-0192", title: "Kampagne Q3", date: day(-19), due_date: day(11),
            status: "sent", net_total: 7500, gross_total: 8925, customer: { id: 303, name: "Kliniken Nordwest" } },
        { id: 5108, identifier: "R2026-0193", title: "Fotoproduktion", date: day(-19), due_date: day(11),
            status: "sent", net_total: 4200, gross_total: 4998, customer: { id: 303, name: "Kliniken Nordwest" } },
        { id: 5109, identifier: "R2026-0194", title: "Retainer August", date: day(-19), due_date: day(11),
            status: "sent", net_total: 8800, gross_total: 10472, customer: { id: 303, name: "Kliniken Nordwest" } },
        { id: 5112, identifier: "R2026-0201", title: "Retainer September", date: day(-6), due_date: day(24),
            status: "sent", net_total: 16000, gross_total: 19040, customer: { id: 301, name: "Weinberg & Co. KG" } },
        { id: 5115, identifier: "R2026-0208", title: "Designsystem", date: day(2), due_date: day(32),
            status: "created", net_total: 21000, gross_total: 24990, customer: { id: 302, name: "Hoffmann Bau GmbH" } },
        // Reverse-Charge: Kunde in Österreich, 0 % Umsatzsteuer
        { id: 5118, identifier: "R2026-0211", title: "Beratung Wien", date: day(-11), due_date: day(19),
            status: "sent", net_total: 6400, gross_total: 6400, customer: { id: 304, name: "Almgruber GmbH" } },
        // Entwurf: darf nicht in der Prognose auftauchen
        { id: 5120, identifier: "R2026-0215", title: "Noch nicht raus", date: day(1), due_date: day(31),
            status: "draft", net_total: 30000, gross_total: 35700, customer: { id: 302, name: "Hoffmann Bau GmbH" } },
    ];
    const purchasesRaw = [
        { id: 8801, identifier: "E2026-0071", date: day(-14), due_date: day(6), net_total: 2857.14, gross_total: 3400,
            company: { id: 901, name: "Kruse Entwicklung" }, iban: "DE02120300000000202051" },
        { id: 8804, identifier: "E2026-0074", date: day(-9), due_date: day(19), net_total: 1900, gross_total: 2261,
            company: { id: 902, name: "Druckerei Aurel" } },
        { id: 8807, identifier: "E2026-0079", date: day(-3), due_date: day(34), net_total: 3514.29, gross_total: 4182,
            company: { id: 901, name: "Kruse Entwicklung" }, iban: "DE02120300000000202051" },
        // Reverse-Charge-Einkauf aus der EU
        { id: 8810, identifier: "E2026-0082", date: day(-7), due_date: day(14), net_total: 1800, gross_total: 1800,
            company: { id: 903, name: "Figma Ireland Ltd" }, items: [{ vat_code: { reverse_charge: true } }] },
    ];
    const fixedCostsRaw = [
        { id: 77, title: "Büromiete", deadline: 3,
            costs: [0, 1, 2, 3, 4, 5].map((m) => ({ year: Number(addM(m).slice(0, 4)), month: Number(addM(m).slice(5, 7)), amount: 1500 })) },
        { id: 78, title: "Software & Lizenzen", deadline: 13,
            costs: [0, 1, 2, 3, 4, 5].map((m) => ({ year: Number(addM(m).slice(0, 4)), month: Number(addM(m).slice(5, 7)), amount: 1200 })) },
        { id: 79, title: "Gehälter",
            costs: [0, 1, 2, 3, 4, 5].map((m) => ({ year: Number(addM(m).slice(0, 4)), month: Number(addM(m).slice(5, 7)), amount: 11350 })) },
        { id: 80, title: "Versicherungen", deadline: 9,
            costs: [0, 1, 2, 3, 4, 5].map((m) => ({ year: Number(addM(m).slice(0, 4)), month: Number(addM(m).slice(5, 7)), amount: 1000 })) },
    ];
    function addM(n) {
        const d = new Date(`${today.slice(0, 7)}-01T00:00:00Z`);
        return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + n, 1)).toISOString().slice(0, 7);
    }
    const mappings = {
        "77": { id: "77", title: "Büromiete", amountIsGross: false, vatRatePercent: 19, reverseCharge: false,
            category: "rent", paymentDayOfMonth: 3, splitPayroll: false,
            payrollNetSharePercent: 0, payrollWageTaxSharePercent: 0, payrollSocialSharePercent: 0, enabled: true },
        "78": { id: "78", title: "Software & Lizenzen", amountIsGross: true, vatRatePercent: 19, reverseCharge: false,
            category: "software", paymentDayOfMonth: 13, splitPayroll: false,
            payrollNetSharePercent: 0, payrollWageTaxSharePercent: 0, payrollSocialSharePercent: 0, enabled: true },
        "79": { id: "79", title: "Gehälter", amountIsGross: true, vatRatePercent: 0, reverseCharge: false,
            category: "personnel", paymentDayOfMonth: 28, splitPayroll: true,
            payrollNetSharePercent: 60, payrollWageTaxSharePercent: 15, payrollSocialSharePercent: 25, enabled: true },
        "80": { id: "80", title: "Versicherungen", amountIsGross: true, vatRatePercent: 0, reverseCharge: false,
            category: "insurance", paymentDayOfMonth: 9, splitPayroll: false,
            payrollNetSharePercent: 0, payrollWageTaxSharePercent: 0, payrollSocialSharePercent: 0, enabled: true },
    };
    const bankRaw = [
        // Teilzahlung mit Rechnungsnummer — soll sicher matchen
        { id: "tx-9001", currency: "EUR", creditDebitIndicator: "CRDT", amount: "5000.00", bookingDate: day(-4),
            debtorName: "WEINBERG U CO KG", debtorIban: "DE44500105175407324931", remittanceInformation: "R2026-0151 Teilzahlung" },
        // Sammelzahlung über drei Rechnungen
        { id: "tx-9002", currency: "EUR", creditDebitIndicator: "CRDT", amount: "24395.00", bookingDate: day(-1),
            debtorName: "KLINIKEN NORDWEST GGMBH", debtorIban: "DE21500105172345678901",
            remittanceInformation: "Sammelueberweisung R2026-0192 R2026-0193" },
        // Fixkosten ohne Referenz — landet in der Inbox
        { id: "tx-9003", currency: "EUR", creditDebitIndicator: "DBIT", amount: "1428.00", bookingDate: day(-2),
            creditorName: "ADOBE SYSTEMS SOFTWARE IRLD", remittanceInformation: "Rechnung Abo" },
        // Miete, sauber erkennbar
        { id: "tx-9004", currency: "EUR", creditDebitIndicator: "DBIT", amount: "1785.00", bookingDate: day(-16),
            creditorName: "HV SONNENSCHEIN GMBH", remittanceInformation: "Miete Buero" },
        // Eingangsrechnung mit Referenz
        { id: "tx-9005", currency: "EUR", creditDebitIndicator: "DBIT", amount: "3400.00", bookingDate: day(-1),
            creditorName: "KRUSE ENTWICKLUNG", creditorIban: "DE02120300000000202051", remittanceInformation: "E2026-0071" },
        // Ungeplante Kleinausgaben — speisen den Restkosten-Puffer
        ...[6, 13, 21, 29, 37, 44, 52, 61, 73, 88].map((d, i) => ({
            id: `tx-95${String(i).padStart(2, "0")}`, currency: "EUR", creditDebitIndicator: "DBIT",
            amount: String(60 + i * 27), bookingDate: day(-d), creditorName: ["DB VERTRIEB", "AMAZON EU", "TANKSTELLE", "SUPERMARKT"][i % 4],
            remittanceInformation: "Kartenzahlung",
        })),
    ];
    // ---- Aufbereiten ------------------------------------------------------------
    const now = new Date().toISOString();
    const settings = {
        vat: { scheme: "soll", period: "monthly", permanentExtension: false,
            previousYearLiabilityCents: 0, taxOfficeIban: "DE12500105170648489890" },
        bankHolidayState: "NW", minimumBalanceCents: 1500000, alertEmails: [],
        residualBufferEnabled: true, residualBufferLookbackMonths: 6,
        mocoWritebackAuto: false, applyPaymentBehaviour: true, updatedAt: now,
    };
    const planned = [];
    for (const raw of invoicesRaw) {
        const i = mapInvoice(raw, { now });
        if (i)
            planned.push(i);
    }
    for (const raw of purchasesRaw) {
        const i = mapPurchase(raw, { now });
        if (i)
            planned.push(i);
    }
    for (const raw of fixedCostsRaw) {
        planned.push(...expandFixedCost(raw, mappings[String(raw.id)], { from: addM(0), to: addM(11) }, { now, state: "NW" }));
    }
    planned.push(...vatPlannedItems(computeVatPeriods(planned, settings.vat, { state: "NW" }), settings.vat));
    const transactions = bankRaw.map((t) => mapBankTransaction(t, "acc-giro", { now }));
    const candidates = proposeMatches({ plannedItems: planned, bankTransactions: transactions });
    const matches = candidates.map((c) => ({
        id: `${c.plannedItemId}__${c.bankTransactionId}`,
        plannedItemId: c.plannedItemId, bankTransactionId: c.bankTransactionId,
        amountCents: c.amountCents, tier: c.tier, confidence: c.confidence,
        status: (c.confidence >= AUTO_MATCH_THRESHOLD ? "auto" : "suggested"),
        reasons: c.reasons, createdAt: now,
        decidedAt: c.confidence >= AUTO_MATCH_THRESHOLD ? now : null,
        decidedBy: c.confidence >= AUTO_MATCH_THRESHOLD ? "system" : null,
        writtenBackToMoco: false,
    }));
    // Automatisch gebuchte Zuordnungen anrechnen — ueber dieselbe Regel wie im
    // echten Sync, sonst zeigt die Demo Zahlen, die es so nie geben kann.
    const perItem = new Map();
    const perTx = new Map();
    for (const m of matches.filter((x) => x.status === "auto")) {
        perItem.set(m.plannedItemId, (perItem.get(m.plannedItemId) ?? 0) + m.amountCents);
        perTx.set(m.bankTransactionId, (perTx.get(m.bankTransactionId) ?? 0) + m.amountCents);
    }
    for (const item of planned) {
        item.matchedCents = resolveMatchedCents(item, perItem.get(item.id) ?? 0);
        item.status = statusFor(item, item.matchedCents);
    }
    for (const tx of transactions) {
        tx.matchedCents = Math.min(perTx.get(tx.id) ?? 0, Math.abs(tx.amountCents));
        tx.matchState = tx.matchedCents >= Math.abs(tx.amountCents) ? "matched"
            : tx.matchedCents > 0 ? "partiallyMatched" : "unmatched";
    }
    return {
        settings,
        accounts: [{
                id: "acc-giro", displayName: "Gesch\u00e4ftskonto", iban: "DE89370400440532013000",
                bankName: "Commerzbank", balanceCents: 4851200, balanceDate: today,
                needsReconnect: false, updatedAt: now,
            }],
        fixedCostMappings: mappings,
        plannedItems: planned,
        transactions,
        matches,
        profiles: [
            { id: "301", companyId: "301", displayName: "Weinberg & Co. KG",
                aliases: ["WEINBERG U CO KG"], ibans: ["DE44500105175407324931"],
                averageDelayDays: 26, medianDelayDays: 24, sampleCount: 9, updatedAt: now },
            { id: "303", companyId: "303", displayName: "Kliniken Nordwest",
                aliases: ["KLINIKEN NORDWEST GGMBH"], ibans: ["DE21500105172345678901"],
                averageDelayDays: 4, medianDelayDays: 3, sampleCount: 12, updatedAt: now },
        ],
        scenarios: [
            { id: "baseline", name: "Realistisch", description: "", isBaseline: true,
                colorToken: "s1", adjustments: [], createdAt: now, updatedAt: now },
            { id: "cautious", name: "Vorsichtig",
                description: "Alle Kunden zahlen 21 Tage sp\u00e4ter, Umsatz 15 % niedriger",
                isBaseline: false, colorToken: "s2",
                adjustments: [
                    { kind: "globalDelay", days: 21 },
                    { kind: "globalRevenueScale", percent: 85 },
                ],
                createdAt: now, updatedAt: now },
        ],
        syncRun: {
            startedAt: now, finishedAt: now, bankAccounts: 1, bankTransactions: transactions.length,
            plannedItems: planned.length,
            autoMatched: matches.filter((m) => m.status === "auto").length,
            needsReview: matches.filter((m) => m.status === "suggested").length,
            consentWarnings: [], errors: [],
        },
    };
}
//# sourceMappingURL=demo.js.map